import React, { useMemo, useState } from "react";
import { Info, X } from "phosphor-react";
import Tooltip from "@mui/material/Tooltip";

// Context
import { useExcelFileUploadContext } from "../../context/ExcelFileUploadProvider";

// Components
import Select from "../Inputs/Select";
import StackStyle from "../Styled/Stack.style";
import TableNoSelect from "../Table/TableNoSelect";
import QueueStyle from "../Styled/Queue.style";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Icon,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { CancelButton } from "../Controls/CancelButton";
import { ConfirmButton } from "../Controls/ConfirmButton";
import {
  useModalHistory,
  ACTIONS as MODAL_HISTORY_ACTIONS,
} from "../../context/ModalHistoryProvider";
import useType, { TYPES } from "../../utils/hooks/useType";
import {
  SCHEME_DEAL_TABLE_HEADERS,
  LIST_PRICE_DEAL_TABLE_HEADERS,
} from "../../utils/constants/TABLEHEADERS";
import {
  DEAL_STATUSES,
  DEAL_TYPES,
  API_RESPONSE,
  USER_KEY,
} from "../../utils/constants/SYSTEM";
import { useEffect } from "react";
import { get } from "../../utils/helpers/storage";
import parseDecimal from "../../utils/helpers/parseDecimal";
import api from "../../api/api";

import {
  useDealsContext,
  ACTIONS as DEAL_ACTIONS,
} from "../../context/DealsProvider";
import getDate from "../../utils/helpers/getDate";
import Dialog from "@mui/material/Dialog";
import TableRadioSelect from "../Table/TableRadioSelect";
import { SubmitButton } from "../Controls/SubmitButton";
import ContainerStyle from "../Styled/Container.style";
import calculateVAT, { VAT_TYPES } from "../../utils/helpers/calculateVAT";
import { useSystemContext } from "../../context/SystemProvider";
import { excelDateToJSDate } from "../../utils/helpers/excelDateToJSDate";
import useToast, { TOAST_TYPES } from "../../utils/hooks/useToast";
import { SelectInput } from "../Controls/SelectInput";
import hasNoValue from "../../utils/helpers/hasNoValue";
import { getObjectDiff } from "../../utils/helpers/findObjectDifferences";

const BULK_ACTIONS = {
  INSERT: "Insert all",
  UPDATE: "Update all"
}
/**
 *
 * @param {scope} InstalmentPrice|ListPrice
 * @displays Table with headers to select which system variables will be mapped
 * @functionality Uses {scope} to determine which table headers and which API's to use
 * @functionality Auto matche headers to system variables if their names are the same
 * @functionality Adds validation to certain columns such as 'Advertised Amount'
 * @functionality Cannot move forward until validations are passed and all headers are mapped to system variables
 * @functionality Submit: Check if any MM Code API calls return multiple results, display table if there are
 * @displays MM Code multi results table. Select which MM Code response to use
 * @functionality Submit: Check if any Model ID's from MM Code response are already existing deals, display table if there are
 * @displays Duplicate deal exists table. Toggle whether to insert or update each duplicate
 * @functionality Submit: Create data structure and insert/duplicate into 'PreloadedDeals' DB table
 */
export default function MatchHeaders({ scope }) {
  const user = get(USER_KEY);
  
  const [mayProceed, setMayProceed] = useType("boolean", false);
  const [isSaving, setIsSaving] = useType("boolean", false);

  const [{ excelHeaders, excelRows, excelFile, excelSheet }] = useExcelFileUploadContext();
  console.log('excelFile', excelFile);
  const [rows, setRows] = useType(TYPES.ARRAY, []);

  const [bulkAction, setBulkAction] = useType(TYPES.STRING, "");
  const handleBulkActionChange = (action) => {
    const copyMMCodeResultsData = [...mmCodeResultsTableData];
    const result = copyMMCodeResultsData?.map((mm) => {
      if(!mm.preloadedDeal_ID) {
        console.count("No preloadedDeal_ID");
        return {
          ...mm,
          shouldDuplicate: true,
        };
      }
      return {
        ...mm,
        shouldDuplicate: action === BULK_ACTIONS.INSERT,
      };
    });

    console.log('result', result);
    setMMCodeResultsTableData(result);
    setBulkAction(action);
  }

  const [showMMCodeResultsTable, setShowMMCodeResultsTable] = useType(
    "boolean",
    false
  );
  const [showDuplicateDealsTable, setShowDuplicateDealsTable] = useType(
    "boolean",
    false
  );

  const [mmCodeResultsTableData, setMMCodeResultsTableData] = useType(
    "array",
    []
  );

  const [mmCodeResultItemID, setMMCodeResultItemID] = useType("string", "");

  //const [, setMatchToCarContext] = useMatchToCarContext();
  const [, dispatchDeals] = useDealsContext();
  const [, setModalHistoryContext] = useModalHistory();
  const popEnd = () =>
    setModalHistoryContext({ type: MODAL_HISTORY_ACTIONS.POP_END });
  const popAll = () =>
    setModalHistoryContext({ type: MODAL_HISTORY_ACTIONS.POP_ALL });
  const [addToast] = useToast();
  const [{ vat },] = useSystemContext();

  const headers =
    scope === DEAL_TYPES.INSTALMENT_PRICE
      ? SCHEME_DEAL_TABLE_HEADERS
      : LIST_PRICE_DEAL_TABLE_HEADERS;

  // Item for validation
  const advertisedInstalmentName = excelHeaders.find((h) =>
    h.includes("Advertised")
  );
  const advertisedInstalmentIndex = excelHeaders.indexOf(
    advertisedInstalmentName
  );
  const modelName = excelHeaders.find((h) =>
    h.includes("Model")
  );
  const modelIndex = excelHeaders.indexOf(
    modelName
  );
  const expiryDateName = excelHeaders.find((h) =>
    h.toLowerCase().includes("expiry date")
  );
  const expiryDateIndex = excelHeaders.indexOf(
    expiryDateName
  );
  const mmCodeName = excelHeaders.find((h) =>
    h.toLowerCase().includes("m&m")
  );
  const mmCodeIndex = excelHeaders.indexOf(
    mmCodeName
  );
  const notesName = excelHeaders.find((h) =>
    h.toLowerCase().includes("notes")
  );
  const notesIndex = excelHeaders.indexOf(
    notesName
  );

  const currentMMCodeResultData = useMemo(
    () =>
      mmCodeResultsTableData?.filter(
        (result) => result?.completed === false
      )[0],
    [mmCodeResultsTableData]
  );

  // Auto match headers
  const getHeaderMatch = (systemHeader) => {
    let match = undefined;
    // excelHeaders -> headers from upload
    excelHeaders.forEach((uploadedHeader, index) => {
      if (uploadedHeader.toLowerCase() === systemHeader.toLowerCase()) {
        match = index;
      }
    });
    return match;
  };

  const [matchedHeaders, setMatchedHeaders] = useState(
    // headers -> predefined headers from SYSTEM
    headers
      .map((header) =>
        Object.keys(header).map((key) => ({
          key,
          option: header[key],
          excelIndex: getHeaderMatch(header[key]),
        }))
      )
      .flat()
  );

  // Set "mayProceed" to true if all "matchTo" fields have been populated
  useEffect(() => {
    const hasNoEmptyMatchTo =
      matchedHeaders.filter((h) => h.excelIndex === undefined).length === 0;

    const hasNoErrors = rows?.map(row => row?.filter(r => r.error))?.flat()?.length === 0;

    if (hasNoEmptyMatchTo && hasNoErrors) setMayProceed(true);
    else setMayProceed(false);
  }, [matchedHeaders, rows, setMayProceed]);

  // Use excel headers and include a select input
  const headersWithSelect = useMemo(
    () =>
      excelHeaders.map((h, index) => (
        <StackStyle width="max-content" gap="5px">
          {h}
          <Select
            value={matchedHeaders.find((h) => h?.excelIndex === index)?.option}
            options={matchedHeaders.map((h) => h?.option)}
            handleChange={(option) => {
              // Find already used excel index and unmap it
              matchedHeaders.forEach((h, updateIndex) => {
                if (h?.excelIndex === index && h?.option !== option) {
                  // Already used index
                  // Unmap excel index to system field
                  const newMatchedHeaders = Array.from(matchedHeaders);
                  newMatchedHeaders.splice(updateIndex, 1, {
                    key: h?.key,
                    option: h?.option,
                    excelIndex: undefined,
                  });
                  setMatchedHeaders(newMatchedHeaders);
                }
              });

              // Find excel index to map it
              setMatchedHeaders((prev) => {
                let result = [];
                prev.forEach((h, updateIndex) => {
                  if (h?.option === option) {
                    // Get excel index and map to system field
                    result = Array.from(prev);
                    result.splice(updateIndex, 1, {
                      key: h?.key,
                      option: h?.option,
                      excelIndex: index,
                    });
                  }
                });
                return result;
              });
            }}
            width="180px"
            prefix={
              !matchedHeaders.find((h) => h.excelIndex === index) && (
                <Tooltip title="Please select a match for this column">
                  <Icon>
                    <X weight="thin" color="red" size={18} />
                  </Icon>
                </Tooltip>
              )
            }
          />
        </StackStyle>
      )),
    [excelHeaders, matchedHeaders, setMatchedHeaders]
  );

  console.log('modelIndex', modelIndex);
  console.log('excelRows', excelRows);

  const rowsWithValidation = useMemo(
    () =>
      excelRows?.filter((r) => r[modelIndex]?.length > 0)?.map((r) => {
        const row = Promise.all(r.map(async (value, index) => {
          if (index === advertisedInstalmentIndex) {
            // Advertised amount cannot be 0 or empty
            if (
              value === 0 ||
              value === undefined ||
              value === null ||
              value === ""
            ) {
              setMayProceed(false);
              return {
                label: (
                  <Tooltip title="Advertised instalment cannot be 0 or empty. Please fix the issue in the excel file and re-upload.">
                    <QueueStyle justifyContent="start" gap="30px">
                      <Info weight="thin" color="red" size={18} />
                      {value}
                    </QueueStyle>
                  </Tooltip>
                ),
                value,
                error: true,
              };
            }

            // Advertised amount less than 1000 warning
            if (value < 1000) {
              return {
                label: (
                  <Tooltip title="Advertised instalment less than 1000. Are you sure this is correct?">
                    <QueueStyle justifyContent="start" gap="30px">
                      <Info weight="thin" color="orange" size={18} />
                      {value}
                    </QueueStyle>
                  </Tooltip>
                ),
                value,
                error: true,
              };
            }

            // TODO
            // Installment - amount can't 0 or empty
            // Installment - cannot be less than 1000
            // Residual - amount can't 0 or empty
            // Rate to client - amount can't 0 or empty
            // List price - amount can't 0 or empty
            // Decimals - make sure it is correct decimal format "0.1" not "0,1"
            // When uploading sheet, only store historical if it was successful
            // Give Shandelle and Tyler own login details
            // Show history of individual updates
            // Change checking if deal exists using deposit to some other method to be more dynamic since VW is uploading arbritrary deposits such as 10.8%

            // Advertised amount cannot be 300 more or less
            // TODO: Replace "300" values with real value
            // if(value > 300 || value < 300) {
            //   setMayProceed(false);
            //   return {
            //     label: (
            //       <ErrorStyle title="Advertised amount cannot be 300 more or less. Please fix the issue and re-upload.">
            //         {value}
            //       </ErrorStyle>
            //     ),
            //     value,
            //     error: true,
            //   };
            // }
          }

          // Convert excel date format to JS date format
          if (index === expiryDateIndex) {
            value = getDate(excelDateToJSDate(value))[0];
          }

          // Make sure decimal format is correct "0.1" not "0,1"
          if (value?.toString().startsWith("0,")) {
            value = value?.replace(",", ".");
          }

          // Convert percentage values from 0.1 to 1.0
          if (value?.toString().startsWith("0.")) {
            value = parseFloat((value * 100).toFixed(2));
          }

          // Limit decimal points to 2
          if (parseDecimal(value) && index !== mmCodeIndex && index !== expiryDateIndex && index !== notesIndex) {
            value = parseDecimal(value);
          }

          // Add thousand separator
          if (parseDecimal(value) && value >= 1000 && index !== mmCodeIndex && index !== expiryDateIndex && index !== notesIndex) {
            return {
              label: new Intl.NumberFormat("en-ZA", {}).format(value),
              value: value,
              error: false
            }
          }

          if (index === mmCodeIndex) {
            const mmCodeResp = await api.getCarDataPubResultFromMMCode(value);
            console.log('mmCodeResp', mmCodeResp);
            let label = "";
            if (mmCodeResp?.length > 1) {
              label = `${value} (Multiple results)`;
            } else if (mmCodeResp?.length === 0) {
              label = `${value} (Error)`;
            } else if (mmCodeResp[0]?.status === "Success") {
              label = `${value} ${mmCodeResp[0]?.range} ${mmCodeResp[0]?.model}`
            } else if (mmCodeResp[0]?.status === "Error") {
              label = `${value} (No results)`;
            }

            return {
              label: label,
              value: value,
              error: false
            }
          }

          return { label: value, value, error: false };
        }));

        return row;
      }),
    [excelRows, modelIndex, advertisedInstalmentIndex, expiryDateIndex, mmCodeIndex, notesIndex, setMayProceed]
  );

  useEffect(() => {
    (async () => {
      const rows = await Promise.all(rowsWithValidation);
      console.log('rows', rows);
      setRows(rows);
    })()
  }, [rowsWithValidation, setRows]);

  // Get results from MMCode search, if there are multi results for 1 MMCode, show table to select the correct one, else handle duplicate deals
  const handleMultiResults = async () => {
    setIsSaving(true);
    addToast(TOAST_TYPES.INFO, "Handling multiple results for each MMCode.");
    console.log("handleMultiResults (MMCode)");
    const mmCode = matchedHeaders.find((h) => h.key === "mmCode");
    const modelName = matchedHeaders.find((h) => h.key === "model");
    const deposit = matchedHeaders.find((h) => h.key === "deposit");
    const period = matchedHeaders.find((h) => h.key === "period");

    const dataArray = excelRows.map(async (row, index) => {
      const mmCodeResp = await api.getCarDataPubResultFromMMCode(
        `${row[mmCode?.excelIndex]}`
      );

      return {
        dealName: row[modelName?.excelIndex],
        deposit: parseDecimal(row[deposit?.excelIndex]) * 100,
        period: parseInt(row[period?.excelIndex]),
        mmCodeResp,
        excelIndex: index,
        completed: mmCodeResp?.length > 1 ? false : null,
      };
    });
    Promise.all(dataArray).then((results) => {
      const successfulResults = results?.filter((r) => r?.mmCodeResp?.find(mm => mm?.status !== API_RESPONSE.ERROR));
      setMMCodeResultsTableData(
        successfulResults
      ); // Hold all MM Code successful API data to avoid calling the API again

      console.log('multiResults', results);
      const isMultiResults =
        results?.filter((r) => r?.mmCodeResp?.length > 1)?.length > 0;

      console.log('isMultiResults', isMultiResults);
      console.log('successfulResults', successfulResults);
      if (isMultiResults) setShowMMCodeResultsTable(true);
      else handleDuplicates(successfulResults);
    });
  };

  // Handle selecting correct MMCode result if there are more than 1
  const handleMutliResultItemSelect = () => {
    console.log("handleMultiResultsSelect (MMCode)")

    const copyMMCodeResultsData = [...mmCodeResultsTableData];
    const index = mmCodeResultsTableData?.findIndex(
      (mm) => mm?.completed === false
    );

    const updatedItem = {
      ...currentMMCodeResultData,
      completed: true,
      mmCodeResp: currentMMCodeResultData?.mmCodeResp?.filter(
        (mm) => mm.model_ID === mmCodeResultItemID
      ),
    };
    copyMMCodeResultsData.splice(index, 1, updatedItem);

    setMMCodeResultsTableData(copyMMCodeResultsData);

    if (
      copyMMCodeResultsData?.filter(
        (result) => result?.completed === false
      )[0] === undefined
    ) {
      setShowMMCodeResultsTable(false);
      handleDuplicates(copyMMCodeResultsData);
    }
  };

  // Get results from ModelID & DealType to determine if the deal already exists, show table to select whether to update or insert the duplicate, else handle upload
  const handleDuplicates = (mmCodeResultsData) => {
    addToast(TOAST_TYPES.INFO, "Handling duplicate results for each deal.");
    console.log("handleDuplicates (ModelID + DealType)");
    console.log('mmCodeResultsData', mmCodeResultsData);
    const duplicates = mmCodeResultsData?.map(async (mmCodeResult) => {
      const modelID = mmCodeResult?.mmCodeResp[0]?.model_ID; // There should only be 1 mmCodeResp. Multi results get handle before duplicates
      const deposit = mmCodeResult?.deposit;
      const period = mmCodeResult?.period;
      console.log('deposit', deposit);
      console.log('period', period);

      const duplicateDealChecker = await api.getDealByModelIDAndType(
        modelID,
        scope === DEAL_TYPES.INSTALMENT_PRICE
          ? DEAL_TYPES.INSTALMENT_PRICE
          : scope === DEAL_TYPES.LIST_PRICE && DEAL_TYPES.LIST_PRICE,
        deposit,
        period || 0
      );

      console.log('duplicateDealChecker', duplicateDealChecker);

      const isDuplicateDeal =
        duplicateDealChecker?.message === API_RESPONSE.PRELOADEDDEAL_FOUND;

      return {
        ...mmCodeResult,
        isDuplicate: isDuplicateDeal,
        shouldDuplicate: null,
        preloadedDeal_ID: duplicateDealChecker?.preloadedDeal_ID,
      };
    });
    console.log('duplicates', duplicates);
    Promise.all(duplicates).then((results) => {
      setMMCodeResultsTableData(results);

      const isDuplicates = results?.filter((r) => r?.isDuplicate)?.length > 0;
      console.log('isDuplicates', isDuplicates);
      if (isDuplicates) setShowDuplicateDealsTable(true);
      else uploadeExcelRows(results);
    });
  };

  const mmCodeResultsHeaders = useMemo(
    () =>
      Object.keys(currentMMCodeResultData?.mmCodeResp[0] || [])?.map((key) => ({
        id: key,
        label: key.replace(/[_]/g, " "),
      })),
    [currentMMCodeResultData]
  );
  const mmCodeResultsRows = useMemo(
    () => currentMMCodeResultData?.mmCodeResp || [],
    [currentMMCodeResultData]
  );

  const duplicateDealsHeaders = useMemo(
    () => ["Action", ...excelHeaders],
    [excelHeaders]
  );
  // Holds functionality to select whether to update or insert the duplicate deal
  const duplicateDealsRows = useMemo(() => {
    const handleDuplicateItemToggle = (excelIndex, value) => {
      const copyMMCodeResultsData = [...mmCodeResultsTableData];

      const result = copyMMCodeResultsData?.map((mm) => {
        if (mm?.excelIndex !== excelIndex) return mm;
        return {
          ...mm,
          shouldDuplicate: value,
        };
      });

      setMMCodeResultsTableData(result);
    };

    return mmCodeResultsTableData
      ?.filter((mm) => mm?.isDuplicate)
      ?.map((mm) => [
        <RadioGroup
          aria-labelledby="duplicate-deal-found-group"
          defaultValue="insert"
          name="duplicate-deal-found-radio-buttons-group"
        >
          <FormControlLabel
            value="insert"
            control={
              <Radio
                checked={mm?.shouldDuplicate === true}
                onChange={() => handleDuplicateItemToggle(mm?.excelIndex, true)}
                value="insert"
                name="should-duplicate-deal"
                inputProps={{ "aria-labelledby": "insert" }}
                size="xs"
              />
            }
            label={<Typography sx={{ fontSize: "x-small" }}>Insert</Typography>}
          />
          <FormControlLabel
            value="update"
            control={
              <Radio
                checked={mm?.shouldDuplicate === false}
                onChange={() =>
                  handleDuplicateItemToggle(mm?.excelIndex, false)
                }
                value="update"
                name="should-duplicate-deal"
                inputProps={{ "aria-labelledby": "duplicate" }}
                size="xs"
              />
            }
            label={<Typography sx={{ fontSize: "x-small" }}>Update</Typography>}
          />
        </RadioGroup>,
        ...excelRows[mm?.excelIndex],
      ]);
  }, [mmCodeResultsTableData, excelRows, setMMCodeResultsTableData]);

  // On confirm click
  const uploadeExcelRows = async (results) => {
    console.log("uploadExcelRows")
    const advertisedInstalment = matchedHeaders.find(
      (h) => h.key === "advertisedInstalment"
    );
    const retailInclVAT = matchedHeaders.find((h) => h.key === "retailInclVAT");
    const rateToClient = matchedHeaders.find((h) => h.key === "rateToClient");
    const rateToClientPercent = matchedHeaders.find(
      (h) => h.key === "rateToClientPercent"
    );
    const deposit = matchedHeaders.find((h) => h.key === "deposit");
    const period = matchedHeaders.find((h) => h.key === "period");
    const balloonPercent = matchedHeaders.find(
      (h) => h.key === "balloonPercent"
    );
    const balloon = matchedHeaders.find((h) => h.key === "balloon");
    const totalCostOfCredit = matchedHeaders.find(
      (h) => h.key === "totalCostOfCredit"
    );
    const dealAssistance = matchedHeaders.find(
      (h) => h.key === "dealAssistance"
    );
    const notes = matchedHeaders.find((h) => h.key === "notes");
    const dealStatus = matchedHeaders.find((h) => h.key === "dealStatus");
    const expiryDate = matchedHeaders.find((h) => h.key === "expiryDate");
    const mmCode = matchedHeaders.find((h) => h.key === "mmCode");

    const [, startDateInit] = getDate(new Date());

    // Scheme deals
    if (scope === DEAL_TYPES.INSTALMENT_PRICE) {
      const dataArray = excelRows.map(async (row, index) => {
        const [expiryDateInit] = getDate(excelDateToJSDate(row[expiryDate?.excelIndex]));

        console.log('results', results);
        const currentMMCode = results.find(
          (mm) => mm?.excelIndex === index
        );

        console.log('currentMMCode', currentMMCode);

        if (currentMMCode === undefined) {
          return {
            data: {
              success: false,
              message: `Could not find car based on MM Code: ${
                row[mmCode?.excelIndex]
              }`,
            }
          };
        }

        const mmCodeResp = currentMMCode?.mmCodeResp[0];

        // Build data
        const preloadedDealID = currentMMCode?.preloadedDeal_ID;
        const carID = mmCodeResp?.model_ID;
        const brand = mmCodeResp?.brand;
        const range = mmCodeResp?.range;
        const model = mmCodeResp?.model;
        const transmission = mmCodeResp?.transmission_Type;
        const fuelType = mmCodeResp?.fuel_Type;
        //const vin = mmCodeResp?.vinNumber; // CarDataPub doesn't have a vin field

        const dealName = `Total: R ${parseDecimal(
          row[totalCostOfCredit?.excelIndex]
        )}, Instalment: R ${parseDecimal(
          row[advertisedInstalment?.excelIndex]
        )}, Rate: % ${parseDecimal(row[rateToClientPercent?.excelIndex] * 100)
          }, Deposit: % ${parseDecimal(row[deposit?.excelIndex]) * 100}`;

        const data = {
          ShouldDuplicate: currentMMCode?.shouldDuplicate,
          PreloadedDealID: preloadedDealID,
          CarID: carID,
          UserID: parseInt(get(USER_KEY)?.userID),
          DealStatus: row[dealStatus?.excelIndex] === DEAL_STATUSES.ACTIVE
            ? "QuoteApproved"
            : "QuoteStopped",
          QuoteReusableStartDate: startDateInit,
          QuoteReusableEndDate: `${expiryDateInit}T00:00`,
          DealName: dealName,
          Instalment: parseDecimal(row[advertisedInstalment?.excelIndex]),
          RetailIncl: parseDecimal(row[retailInclVAT?.excelIndex]),
          RateType: row[rateToClient?.excelIndex],
          RatePercent: parseDecimal(row[rateToClientPercent?.excelIndex] * 100),
          Deposit: parseDecimal(row[deposit?.excelIndex] * 100),
          Period: parseInt(row[period?.excelIndex]) || 0,
          BalloonPercent: parseDecimal(row[balloonPercent?.excelIndex] * 100),
          BalloonAmount: parseDecimal(row[balloon?.excelIndex]),
          TotalCostOfCredit: parseDecimal(row[totalCostOfCredit?.excelIndex]),
          DealAssistance: parseDecimal(row[dealAssistance?.excelIndex]),
          Notes: row[notes?.excelIndex],
          SearchBarcode: `${brand}_${range}_${model}_${
            row[advertisedInstalment?.excelIndex]
          }_${row[mmCode?.excelIndex]}_${row[notes?.excelIndex]}`,
          DealJSON: JSON.stringify({
            "dealname-id": dealName,
            submitType: "goLive",
            currentDealType: "Live",
            PreloadedDealsID: "", // Will only have if updating deal
            DealStatus: row[dealStatus?.excelIndex],
            ToggleStatus: "",
            dealer_comments: row[notes?.excelIndex],
            quoteValidity: "",
            PLDType: "InstalmentPrice",
            "car-section": {
              brand: brand,
              range: range,
              model: model,
              gearshift: transmission,
              fueltype: fuelType,
              variant: carID,
              dealer_id: "",
              user_id: get(USER_KEY)?.userID,
              car_status: "New",
              mileage: "0",
              vin: "",
              color: "Various"
            },
            "options-section": [],
            "additional-fees": [],
            "price-section": {
              standard_price: parseDecimal(calculateVAT(VAT_TYPES.EXCL, row[retailInclVAT?.excelIndex], vat)),
              discount: 0,
              discount_percent: 0,
              dealer_price: 0,
              total_extras: 0,
              on_road_costs: 0,
              other_costs: 0,
              othercosts_reason: "",
              total_excl: parseDecimal(calculateVAT(VAT_TYPES.EXCL, row[retailInclVAT?.excelIndex], vat)),
              nonVAT_costs: 0,
              nonVAT: 0,
              vat: 0,
              total_total: parseDecimal(row[retailInclVAT?.excelIndex]),
              "spotter-fee": 0
            },
            "delivery-section": {},
            dealer_attachments: [],
            "validity-section": {
              quoteSentDate: "",
              quoteValidFor: "1",
              quoteToOtherCustomers: "2",
              quoteReusableStartDate: startDateInit,
              quoteReusableEndDate: expiryDateInit + "T00:00",
              internalNotes: "Portal_Upload",
              NoStock: "",
              NoStockUntilDate: "",
              NoStockComments: ""
            },
            totals: {
              standard_price: 0,
              discount: 0,
              discount_percent: 0,
              price: 0,
              on_road_costs: 0,
              other_costs: 0,
              extras: 0,
              excl: 0,
              total: 0,
              vat: 0,
              nonVAT: 0,
              incl: 0,
              trade_in_offer: 0,
              trade_in_assist: 0,
              trade_in: 0,
              net: 0
            },
            instalment: {
              Instalment: row[advertisedInstalment?.excelIndex],
              InstalmentFee: 0,
              Instalment_ExclFee: row[advertisedInstalment?.excelIndex],
              Instalment_InclFee: row[advertisedInstalment?.excelIndex],
              Retail_Incl: row[retailInclVAT?.excelIndex],
              RateType: row[rateToClient?.excelIndex],
              RatePercent: parseDecimal(row[rateToClientPercent?.excelIndex] * 100),
              Deposit: parseDecimal(row[deposit?.excelIndex] * 100),
              Period: row[period?.excelIndex],
              BalloonPercent: parseDecimal(row[balloonPercent?.excelIndex] * 100),
              BalloonAmount: parseDecimal(row[balloon?.excelIndex]),
              TotalCostOfCredit: parseDecimal(row[totalCostOfCredit?.excelIndex]),
              OEMSubversion: 0,
              DealerSubversion: 0,
              PIC_Code: "",
              ApprovedCAT: "",
              SchemeDealName: dealName,
              Comments: row[notes?.excelIndex]
            },
            dealer_comments_excerpt: row[notes?.excelIndex]
          })
        };
        console.log("scheme data", data);

        let oldData = null;
        if(data.PreloadedDealID) {
          oldData = await api.getNewSchemeDealInfo(data.PreloadedDealID);
        }
        const newData = {
          mmCode: row[mmCode?.excelIndex]?.toString(),
          brand,
          range,
          model,
          dealStatus: row[dealStatus?.excelIndex] === DEAL_STATUSES.ACTIVE
            ? "QuoteApproved"
            : "QuoteStopped",
          startDate: startDateInit,
          endDate: `${expiryDateInit}T00:00`,
          notes: row[notes?.excelIndex],
          dealAssistance: parseDecimal(row[dealAssistance?.excelIndex]),
          instalment: parseDecimal(row[advertisedInstalment?.excelIndex]),
          retailIncl: parseDecimal(row[retailInclVAT?.excelIndex]),
          rateType: row[rateToClient?.excelIndex],
          ratePercent: parseDecimal(row[rateToClientPercent?.excelIndex] * 100),
          deposit: parseDecimal(row[deposit?.excelIndex] * 100),
          period: parseInt(row[period?.excelIndex]) || 0,
          balloonPercent: parseDecimal(row[balloonPercent?.excelIndex] * 100),
          balloonAmount: parseDecimal(row[balloon?.excelIndex]),
        }
        return { data, oldData, newData };
      });
      console.log('dataArray', dataArray);
      const responseArray = await Promise.all(dataArray);
      console.log('responseArray', responseArray);
      const handleUpsertResp = await Promise.all(responseArray?.map(async ({ data }) => {
        console.log('data', data);
        let row = await data;
        console.log('row', row);
        if (row?.success === false) {
          return {
            success: false,
            message: row?.message,
          };
        }

        console.log('row?.ShouldDuplicate', row?.ShouldDuplicate);

        if (row?.ShouldDuplicate === false) {
          console.log('updateNewSchemeDeal row', row);
          const resp = await api.updateNewSchemeDeal(row);
          console.log("updateNewSchemeDeal resp", resp);
          return resp;
        } else if (
          row?.ShouldDuplicate === true ||
          row?.ShouldDuplicate === null ||
          row?.ShouldDuplicate === undefined
        ) {
          console.log('insertNewSchemeDeal row', row);
          const resp = await api.insertNewSchemeDeal(row);
          console.log("insertNewSchemeDeal resp", resp);
          return resp;
        }
      }));
      console.log('handleUpsertResp', handleUpsertResp);
      await Promise.all(handleUpsertResp).then((resp) => {
        console.log("Promise.all Scheme", resp);
        const successfulResultsCount = resp?.filter(r => r?.success)?.length;
        addToast(TOAST_TYPES.SUCCESS, `${successfulResultsCount}/${resp?.length} successful`, { duration: 1000000 });
        resp?.forEach((r) => {
          if (r?.success) {
            dispatchDeals({
              type: DEAL_ACTIONS.REFETCH_DEALS,
              payload: true,
            });
            popAll();
          } else {
            addToast(TOAST_TYPES.ERROR, r?.message);
          }
        });
        setIsSaving(false);
      }).finally(async () => {
        const rows = await Promise.all(dataArray);
        console.log('.finally rows', rows);

        rows?.forEach(async ({oldData, newData}) => {
          if(hasNoValue(oldData) || hasNoValue(newData)) return;
          await insertSchemeDealAudit(oldData, newData);
        });
        await api.checkSchemeDealsForIssues(user?.emailAddress, excelFile?.name, { deals: rows?.map(row => row?.data)?.filter(row => row !== null && row !== undefined && row?.success !== false) });

        const form = new FormData();
        form.append("File", excelFile);
        form.append("FileName", excelFile?.name);
        form.append("SheetName", excelSheet);
        form.append("DealType", "InstallmentPrice");
        form.append("UserEmail", user?.emailAddress);
        await api.insertExcelFileHistory(form);
      });
      // List price deals
    } else if (scope === DEAL_TYPES.LIST_PRICE) {
      const dataArray = excelRows.map(async (row, index) => {
        const [expiryDateInit] = getDate(excelDateToJSDate(row[expiryDate?.excelIndex]));

        const currentMMCode = mmCodeResultsTableData.find(
          (mm) => mm?.excelIndex === index
        );

        if (currentMMCode === undefined) {
          return {
            data: {
              success: false,
              message: `Could not find car based on MM Code: ${
                row[mmCode?.excelIndex]
              }`,
            }
          };
        }

        const mmCodeResp = currentMMCode?.mmCodeResp[0];

        const preloadedDealID = currentMMCode?.preloadedDeal_ID;
        const carID = mmCodeResp?.model_ID;
        const brand = mmCodeResp?.brand;
        const range = mmCodeResp?.range;
        const model = mmCodeResp?.model;
        const transmission = mmCodeResp?.transmission;
        const fuelType = mmCodeResp?.fuel;
        //const vin = mmCodeResp?.vinNumber; // CarDataPub doesn't a vin field

        const dealName = `Total: ${row[retailInclVAT?.excelIndex]}`;

        const data = {
          ShouldDuplicate: currentMMCode?.shouldDuplicate,
          PreloadedDealID: preloadedDealID,
          CarID: carID,
          UserID: parseInt(get(USER_KEY)?.userID),
          DealStatus:
            row[dealStatus?.excelIndex] === DEAL_STATUSES.ACTIVE
              ? "QuoteApproved"
              : "QuoteStopped",
          DealRequestAmount: parseDecimal(row[retailInclVAT?.excelIndex]),
          QuoteReusableStartDate: startDateInit,
          QuoteReusableEndDate: `${expiryDateInit}T00:00`,
          DealName: dealName,
          DealAssistance: parseDecimal(row[dealAssistance?.excelIndex]),
          Notes: row[notes?.excelIndex],
          SearchBarcode: `${brand}_${range}_${model}_${
            parseDecimal(row[retailInclVAT?.excelIndex])
          }_${row[mmCode?.excelIndex]}_${row[notes?.excelIndex]}`,
          DealJSON: JSON.stringify({
            "dealname-id": dealName,
            submitType: "goLive",
            currentDealType: "Live",
            PreloadedDealsID: "", // Will only have if updating deal
            DealStatus: row[dealStatus?.excelIndex],
            ToggleStatus: "",
            dealer_comments: row[notes?.excelIndex],
            quoteValidity: "",
            PLDType: "ListPrice",
            "car-section": {
              brand: brand,
              range: range,
              model: model,
              gearshift: transmission,
              fueltype: fuelType,
              variant: carID,
              dealer_id: "",
              user_id: get(USER_KEY)?.userID,
              car_status: "New",
              mileage: "0",
              vin: "",
              color: "Various"
            },
            "options-section": [],
            "additional-fees": [],
            "price-section": {
              standard_price: parseDecimal(calculateVAT(VAT_TYPES.EXCL, row[retailInclVAT?.excelIndex], vat)),
              discount: 0,
              discount_percent: 0,
              dealer_price: 0,
              total_extras: 0,
              on_road_costs: 0,
              other_costs: 0,
              othercosts_reason: "",
              total_excl: parseDecimal(calculateVAT(VAT_TYPES.EXCL, row[retailInclVAT?.excelIndex], vat)),
              nonVAT_costs: 0,
              nonVAT: 0,
              vat: 0,
              total_total: parseDecimal(row[retailInclVAT?.excelIndex]),
              "spotter-fee": 0
            },
            "delivery-section": {},
            dealer_attachments: [],
            "validity-section": {
              quoteSentDate: "",
              quoteValidFor: "1",
              quoteToOtherCustomers: "2",
              quoteReusableStartDate: startDateInit,
              quoteReusableEndDate: expiryDateInit + "T00: 00",
              internalNotes: "Portal_Upload",
              NoStock: "",
              NoStockUntilDate: "",
              NoStockComments: ""
            },
            totals: {
              standard_price: 0,
              discount: 0,
              discount_percent: 0,
              price: 0,
              on_road_costs: 0,
              other_costs: 0,
              extras: 0,
              excl: 0,
              total: 0,
              vat: 0,
              nonVAT: 0,
              incl: 0,
              trade_in_offer: 0,
              trade_in_assist: 0,
              trade_in: 0,
              net: 0
            },
            instalment: {
              Instalment: 0,
              InstalmentFee: 0,
              Instalment_ExclFee: 0,
              Instalment_InclFee: 0,
              Retail_Incl: 0,
              RateType: 0,
              RatePercent: 0,
              Deposit: 0,
              Period: 0,
              BalloonPercent: 0,
              BalloonAmount: 0,
              TotalCostOfCredit: 0,
              OEMSubversion: 0,
              DealerSubversion: 0,
              PIC_Code: "",
              ApprovedCAT: "",
              SchemeDealName: "",
              Comments: ""
            },
            dealer_comments_excerpt: row[notes?.excelIndex]
          })
        };
        console.log("list price data", data);

        let oldData = null;
        if(data.PreloadedDealID) {
          oldData = await api.getNewListPriceDealInfo(data.PreloadedDealID);
        }
        const newData = {
          mmCode: row[mmCode?.excelIndex]?.toString(),
          brand,
          range,
          model,
          dealStatus: row[dealStatus?.excelIndex] === DEAL_STATUSES.ACTIVE
            ? "QuoteApproved"
            : "QuoteStopped",
          startDate: startDateInit,
          endDate: `${expiryDateInit}T00:00`,
          notes: row[notes?.excelIndex],
          dealAssistance: parseDecimal(row[dealAssistance?.excelIndex]),
        }
        return { data, oldData, newData };
      });
      const responseArray = await Promise.all(dataArray);
      const handleUpsertResp = await Promise.all(responseArray.map(async ({ data }) => {
        let row = await data;

        if (row?.success === false) {
          return {
            success: false,
            message: row?.message,
          };
        }

        if (row?.ShouldDuplicate === false) {
          console.log('updateNewListPriceDeal row', row);
          const resp = await api.updateNewListPriceDeal(row);
          console.log("updateNewListPriceDeal resp", resp);
          return resp;
        } else if (
          row?.ShouldDuplicate === true ||
          row?.ShouldDuplicate === null ||
          row?.ShouldDuplicate === undefined
        ) {
          console.log('insertNewListPriceDeal row', row);
          const resp = await api.insertNewListPriceDeal(row);
          console.log("insertNewListPriceDeal resp", resp);
          return resp;
        }
      }));
      await Promise.all(handleUpsertResp).then((resp) => {
        console.log("Promise.all List Price", resp);
        const successfulResultsCount = resp?.filter(r => r?.success)?.length;
        addToast(TOAST_TYPES.SUCCESS, `${successfulResultsCount}/${resp?.length} successful`, { duration: 1000000 }); 
        resp?.forEach((r) => {
          if (r?.success) {
            dispatchDeals({
              type: DEAL_ACTIONS.REFETCH_DEALS,
              payload: true,
            });
            popAll();
          } else {
            addToast(TOAST_TYPES.ERROR, r?.message);
          }
        });
        setIsSaving(false);
      }).finally(async () => {
        const rows = await Promise.all(dataArray);
        console.log('.finally rows', rows);
        rows?.forEach(async ({ data, oldData, newData }) => {
          if(hasNoValue(oldData) || hasNoValue(newData)) return;
          await insertListPriceDealAudit(oldData, newData);
        });
        await api.checkListPriceDealsForIssues(user?.emailAddress, excelFile?.name, { deals: rows?.map(row => row?.data)?.filter(row => row !== null && row !== undefined && row?.success !== false) });

        const form = new FormData();
        form.append("File", excelFile);
        form.append("FileName", excelFile?.name);
        form.append("SheetName", excelSheet);
        form.append("DealType", "ListPrice");
        form.append("UserEmail", user?.emailAddress);
        await api.insertExcelFileHistory(form);
      });
    }
  };

  function insertSchemeDealAudit(dealInfo, newDealInfo) {
    const oldData = {
      // UserID: parseInt(get(USER_KEY)?.userID),
      // CarID: dealInfo?.car_ID,

      MMCode: dealInfo?.mM_Code,
      Brand: dealInfo?.brand,
      Range: dealInfo?.range,
      Model: dealInfo?.model,

      DealStatus: dealInfo?.deal_Status === DEAL_STATUSES.ACTIVE ? "QuoteApproved" : "QuoteStopped",
      QuoteReusableStartDate: getDate(new Date(dealInfo?.quote_Reusable_Start_Date))[1],
      QuoteReusableEndDate: getDate(new Date(dealInfo?.quote_Reusable_End_Date))[1],
      // DealName: dealInfo?.deal_Name,
      Notes: dealInfo?.notes,
      // SearchBarcode: dealInfo?.search_Barcode,
      DealAssistance: dealInfo?.deal_Assistance,
      Instalment: dealInfo?.instalment,
      RetailIncl: dealInfo?.retail_Incl,
      RateType: dealInfo?.rate_Type,
      RatePercent: dealInfo?.rate_Percent,
      Deposit: dealInfo?.deposit,
      Period: dealInfo?.period,
      BalloonPercent: dealInfo?.balloon_Percent,
      BalloonAmount: dealInfo?.balloon_Amount,
      // TotalCostOfCredit: dealInfo?.total_Cost_Of_Credit,
    };

    const newData = {
      // UserID: parseInt(get(USER_KEY)?.userID),
      // CarID: carID,

      MMCode: newDealInfo?.mmCode,
      Brand: newDealInfo?.brand,
      Range: newDealInfo?.range,
      Model: newDealInfo?.model,

      DealStatus:
        newDealInfo?.dealStatus,
      QuoteReusableStartDate: newDealInfo?.startDate,
      QuoteReusableEndDate: newDealInfo?.endDate,
      // DealName: newDealInfo?.dealName,
      Notes: newDealInfo?.notes,
      // SearchBarcode: newDealInfo?.`${brand}_${range}_${model}_${instalment}_${mmCode}_${notes}`,
      DealAssistance: newDealInfo?.dealAssistance,
      Instalment: newDealInfo?.instalment,
      RetailIncl: newDealInfo?.retailIncl,
      RateType: newDealInfo?.rateType,
      RatePercent: newDealInfo?.ratePercent,
      Deposit: newDealInfo?.deposit,
      Period: newDealInfo?.period,
      BalloonPercent: newDealInfo?.balloonPercent,
      BalloonAmount: newDealInfo?.balloonAmount,
      // TotalCostOfCredit: parseDecimal(totalCostOfCredit),
    };

    if (!hasNoValue(dealInfo)) {
      console.log('dealInfo', dealInfo);
      console.log('oldData', oldData);
      console.log('newData', newData);
      const differences = getObjectDiff(oldData, newData);
      console.log('differences', differences);
      /*
      public int PreloadedDealID { set; get; }
      public string UserEmail { set; get; }
      public string Operation { set; get; }
      public string Change { set; get; }
      public string OldValue { set; get; }
      public string NewValue { set; get; }
      public string DealType { set; get; }
      */
      differences.forEach(async key => {
        const dataMap = {
          PreloadedDealID: dealInfo?.preloaded_Deals_ID,
          UserEmail: user?.emailAddress,
          Operation: "Update",
          Change: key,
          OldValue: oldData?.[key]?.toString(),
          NewValue: newData?.[key]?.toString(),
          DealType: "InstallmentPrice"
        }
        console.log('DealAudit dataMap', dataMap);

        await api.insertDealAudit(dataMap);
      });
    }
  }

  function insertListPriceDealAudit(dealInfo, newDealInfo) {
    const oldData = {
      // UserID: parseInt(get(USER_KEY)?.userID),

      // CarID: dealInfo?.car_ID,

      MMCode: dealInfo?.mM_Code,
      Brand: dealInfo?.brand,
      Range: dealInfo?.range,
      Model: dealInfo?.model,

      DealStatus: dealInfo?.deal_Status === DEAL_STATUSES.ACTIVE ? "QuoteApproved" : "QuoteStopped",
      QuoteReusableStartDate: getDate(new Date(dealInfo?.quote_Reusable_Start_Date))[1],
      QuoteReusableEndDate: getDate(new Date(dealInfo?.quote_Reusable_End_Date))[1],
      // DealName: dealInfo?.deal_Name,
      Notes: dealInfo?.notes,
      // SearchBarcode: dealInfo?.search_Barcode,
      DealAssistance: dealInfo?.deal_Assistance,
      // Instalment: dealInfo?.instalment,
      // RetailIncl: dealInfo?.retail_Incl,
      // RateType: dealInfo?.rate_Type,
      // RatePercent: dealInfo?.rate_Percent,
      // Deposit: dealInfo?.deposit,
      // Period: dealInfo?.period,
      // BalloonPercent: dealInfo?.balloon_Percent,
      // BalloonAmount: dealInfo?.balloon_Amount,
      // TotalCostOfCredit: dealInfo?.total_Cost_Of_Credit,
    };

    const newData = {
      // UserID: parseInt(get(USER_KEY)?.userID),

      // CarID: carID,

      MMCode: newDealInfo?.mmCode,
      Brand: newDealInfo?.brand,
      Range: newDealInfo?.range,
      Model: newDealInfo?.model,

      DealStatus:
        newDealInfo?.dealStatus,
      QuoteReusableStartDate: newDealInfo?.startDate,
      QuoteReusableEndDate: newDealInfo?.endDate,
      // DealName: newDealInfo?.dealName,
      Notes: newDealInfo?.notes,
      // SearchBarcode: newDealInfo?.`${brand}_${range}_${model}_${listPrice}_${mmCode}_${notes}`,
      DealAssistance: newDealInfo?.dealAssistance,
      // Instalment: parseDecimal(instalment),
      // RetailIncl: parseDecimal(retailIncl),
      // RateType: rateType,
      // RatePercent: parseDecimal(ratePercent),
      // Deposit: parseDecimal(deposit),
      // Period: parseInt(period) || 0,
      // BalloonPercent: parseDecimal(balloonPercent),
      // BalloonAmount: parseDecimal(balloonAmount),
      // TotalCostOfCredit: parseDecimal(totalCostOfCredit),
    };

    if (!hasNoValue(dealInfo)) {
      console.log('dealInfo', dealInfo);
      console.log('oldData', oldData);
      console.log('newData', newData);
      const differences = getObjectDiff(oldData, newData);
      console.log('differences', differences);
      /*
      public int PreloadedDealID { set; get; }
      public string UserEmail { set; get; }
      public string Operation { set; get; }
      public string Change { set; get; }
      public string OldValue { set; get; }
      public string NewValue { set; get; }
      public string DealType { set; get; }
      */
      differences.forEach(async key => {
        const dataMap = {
          PreloadedDealID: dealInfo?.preloaded_Deals_ID,
          UserEmail: user?.emailAddress,
          Operation: "Update",
          Change: key,
          OldValue: oldData?.[key]?.toString(),
          NewValue: newData?.[key]?.toString(),
          DealType: "ListPrice"
        }
        console.log('DealAudit dataMap', dataMap);

        await api.insertDealAudit(dataMap);
      });
    }
  }

  return (
    <>
      <TableNoSelect
        headers={headersWithSelect}
        rows={rows?.map(row => row?.map(r => r.label))}
      />

      <Dialog
        open={showMMCodeResultsTable}
        onClose={() => {
          setShowMMCodeResultsTable(false);
          setIsSaving(false);
        }}
        maxWidth="90%"
      >
        <DialogTitle>Multiple results found for this deal.</DialogTitle>
        <DialogContent>
          <p>Model: {currentMMCodeResultData?.dealName}</p>
          <TableRadioSelect
            headers={mmCodeResultsHeaders}
            rows={mmCodeResultsRows}
            id={"model_ID"}
            resultItemID={mmCodeResultItemID}
            setResultItemID={setMMCodeResultItemID}
          />
        </DialogContent>
        <DialogActions>
          <QueueStyle justifyContent="center">
            <CancelButton onClick={() => {
              setShowMMCodeResultsTable(false);
              setIsSaving(false);
            }} />
            <ConfirmButton
              disabled={!mmCodeResultItemID}
              onClick={() => {
                handleMutliResultItemSelect();
              }}
            />
          </QueueStyle>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showDuplicateDealsTable}
        onClose={() => {
          setShowDuplicateDealsTable(false);
          setIsSaving(false);
        }}
        maxWidth="90%"
      >
        <DialogTitle>Deal(s) already exists.</DialogTitle>
        <DialogContent>
          <p>Select whether to update or insert the duplicate</p>
          <br />
          <SelectInput
            label="Bulk action"
            options={Object.values(BULK_ACTIONS)}
            option={bulkAction}
            setOption={handleBulkActionChange}
          // validate={requiredFields.dealStatus.validate}
          />
          <br />
          <TableNoSelect
            headers={duplicateDealsHeaders}
            rows={duplicateDealsRows}
          />
        </DialogContent>
        <DialogActions>
          <QueueStyle justifyContent="center">
            <CancelButton onClick={() => {
              setShowDuplicateDealsTable(false);
              setIsSaving(false);
            }} />
            <ConfirmButton
              disabled={
                mmCodeResultsTableData.filter(
                  (mm) =>
                    mm?.isDuplicate === true && mm?.shouldDuplicate === null
                )?.length > 0
              }
              onClick={() => {
                setShowDuplicateDealsTable(false);
                uploadeExcelRows(mmCodeResultsTableData);
              }}
            />
          </QueueStyle>
        </DialogActions>
      </Dialog>
      {/* <Dialog
        open={showMMCodeErrorsTable}
        onClose={() => setShowMMCodeErrorsTable(false)}
      >
        <DialogTitle>
          Errors found for this deal ({mmCodeErrorsTableData[0]?.dealName})
        </DialogTitle>
        <DialogContent>
          <TableRadioSelect
            headers={[]}
            rows={[]}
            id={"model_ID"}
            resultItemID={mmCodeResultItemID}
            setResultItemID={setMMCodeResultItemID}
          />
        </DialogContent>
        <DialogActions>
          <QueueStyle justifyContent="center">
            <CancelButton onClick={() => setShowMMCodeErrorsTable(false)} />
            <ConfirmButton
              disabled={!mmCodeResultItemID}
              onClick={() => {
                //handleMutliResultItemSelect();
                setShowMMCodeErrorsTable(false);
              }}
            />
          </QueueStyle>
        </DialogActions>
      </Dialog> */}
      <ContainerStyle>
        <QueueStyle justifyContent="center">
          <CancelButton onClick={() => popEnd()} />
          <SubmitButton
            disabled={!mayProceed || isSaving}
            onClick={handleMultiResults}
            isSaving={isSaving}
          />
        </QueueStyle>
      </ContainerStyle>
    </>
  );
}
